import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
// import { Vibration } from 'react-native';
// import { showMessage } from 'react-native-flash-message';
import { cloneDeep, fromPairs, orderBy, toPairs, map, sum, omitBy } from 'lodash'

import { GroupComment } from '@cobu/types';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { Strings } from '../constants/Strings';

/**
 * Available reaction types
 */
export type ReactionType = 'like' | 'love' | 'clap' | 'smile' | 'laugh';

/**
 * Reaction configuration
 */
export interface ReactionConfig {
  type: ReactionType;
  emoji: string;
  color: string;
  animationType?: 'bounce' | 'tada' | 'pulse';
}

/**
 * Default reaction configurations
 */
export const DEFAULT_REACTIONS: ReactionConfig[] = [
  {
    type: 'like',
    emoji: '👍',
    color: '#00B5D1', // cobuTeal
    animationType: 'bounce',
  },
  {
    type: 'love',
    emoji: '❤️',
    color: '#F32E30', // red
    animationType: 'tada',
  },
  {
    type: 'clap',
    emoji: '👏',
    color: '#6D89ED', // blue
    animationType: 'pulse',
  },
  {
    type: 'smile',
    emoji: '😊',
    color: '#FEC100', // cobuYellow
    animationType: 'bounce',
  },
  {
    type: 'laugh',
    emoji: '😂',
    color: '#FEC100', // cobuYellow
    animationType: 'tada',
  },
];

/**
 * Reaction state interface
 */
interface ReactionState {
  currentReaction: string | null;
  // The reaction we expect after the latest local action; used to avoid
  // reverting optimistic UI before server/props catch up
  targetReaction: string | null;
  reactionsCount: number;
  byReactionUserIds: Record<string, string[]>;
  isReactionInProgress: boolean;
  showReactionPicker: boolean;
  showReactionAnimation: boolean;
  // When true, we keep optimistic state until props reflect targetReaction
  awaitingServerSync: boolean;
}

/**
 * Hook configuration
 */
interface UseReactionsConfig {
  /** The comment to manage reactions for */
  comment: GroupComment;
  /** Current user ID */
  currentUserId: string;
  /** Available reactions (defaults to DEFAULT_REACTIONS) */
  reactions?: ReactionConfig[];
  /** Callback to react to a comment */
  onReactToComment?: (commentId: string, reaction: string | null) => Promise<any>;
  /** Callback when reaction picker should be shown */
  onShowReactionPicker?: (commentId: string) => void;
  /** Callback when reaction modal should be shown */
  onShowReactionModal?: (commentId: string) => void;
}

/**
 * Hook return interface
 */
interface UseReactionsReturn {
  // State
  currentReaction: string | null;
  reactionsCount: number;
  byReactionUserIds: Record<string, string[]>;
  isReactionInProgress: boolean;
  showReactionPicker: boolean;
  showReactionAnimation: boolean;
  reactionsSorted: string[];

  // Actions
  addReaction: (reaction: ReactionType) => Promise<void>;
  removeReaction: () => Promise<void>;
  toggleReactionPicker: () => void;
  showReactionModal: () => void;

  // UI helpers
  getReactionConfig: (type: ReactionType) => ReactionConfig | undefined;
  getReactionColor: (type: ReactionType) => string;
  formatReactionCount: (count: number) => string;
  hasUserReacted: (reaction: ReactionType, userId: string) => boolean;
}

/**
 * Custom hook for managing comment reactions
 * 
 * This hook encapsulates all reaction-related logic including:
 * - Optimistic updates with error handling
 * - Vibration feedback
 * - Animation state management
 * - Reaction picker state
 * - Analytics tracking
 * 
 * @example
 * ```tsx
 * const reactions = useReactions({
 *   comment,
 *   currentUserId: user.uuid,
 *   onReactToComment: (commentId, reaction) => mutateReaction({ commentId, reaction }),
 * });
 * 
 * // Add a reaction
 * <TouchableOpacity onPress={() => reactions.addReaction('like')}>
 *   <Text>{reactions.getReactionConfig('like')?.emoji}</Text>
 * </TouchableOpacity>
 * 
 * // Show current reaction count
 * <Text>{reactions.formatReactionCount(reactions.reactionsCount)}</Text>
 * ```
 */
export const useReactions = ({
  comment,
  currentUserId,
  reactions = DEFAULT_REACTIONS,
  onReactToComment,
  onShowReactionPicker,
  onShowReactionModal,
}: UseReactionsConfig): UseReactionsReturn => {
  // Initialize state from comment
  const [state, setState] = useState<ReactionState>(() => ({
    currentReaction: comment.myReaction || null,
    targetReaction: comment.myReaction || null,
    reactionsCount: comment.reactionsCount || 0,
    byReactionUserIds: comment.byReactionUserIds || {},
    isReactionInProgress: false,
    showReactionPicker: false,
    showReactionAnimation: false,
    awaitingServerSync: false,
  }));

  const cancelTokenRef = useRef<any>(null);

  // Memoized reaction configurations
  const reactionMap = useMemo(() => {
    return reactions.reduce((map, config) => {
      map[config.type] = config;
      return map;
    }, {} as Record<ReactionType, ReactionConfig>);
  }, [reactions]);

  // Sorted reactions by count (descending)
  const reactionsSorted = useMemo(() => {
    return Object.keys(state.byReactionUserIds).filter(
      reaction => state.byReactionUserIds[reaction]?.length > 0
    );
  }, [state.byReactionUserIds]);

  /**
   * Add a reaction to the comment
   */
  const addReaction = useCallback(async (reaction: ReactionType) => {
    if (state.isReactionInProgress) {
      // Cancel any ongoing requests
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }
    }

    // Vibration feedback
    // Vibration.vibrate(50);

    // Analytics tracking
    analyticsService.trackEvent?.(AnalyticsEvent.DID_ADD_REACTION, {
      reaction_type: reaction,
      comment_id: comment.uuid,
    });

    // Store previous state for rollback
    const prevState = cloneDeep(state);

    // Optimistic update
    const newByReactionUserIds = {
      [reaction]: [
        ...(state.byReactionUserIds[reaction] || []),
        currentUserId,
      ],
      ...omitBy(state.byReactionUserIds, (value, key) => key === reaction),
      ...(state.currentReaction && state.byReactionUserIds[state.currentReaction]?.length
        ? {
          [state.currentReaction]: state.byReactionUserIds[state.currentReaction].filter(
            id => id !== currentUserId,
          ),
        }
        : {}),
    };

    const sortedReactionUserIds = fromPairs(
      orderBy(toPairs(newByReactionUserIds), pair => pair[1].length, 'desc'),
    );

    setState({
      ...state,
      currentReaction: reaction,
      reactionsCount: sum(map(sortedReactionUserIds, value => value.length)) || 0,
      byReactionUserIds: sortedReactionUserIds,
      isReactionInProgress: true,
      showReactionAnimation: true,
      targetReaction: reaction,
      awaitingServerSync: true,
    });

    // Hide reaction picker
    if (onShowReactionPicker) {
      onShowReactionPicker('');
    }

    try {
      if (onReactToComment) {
        // Make API call
        await onReactToComment(comment.uuid, reaction);

        // Finalize state (keep optimistic values)
        setState(prev => ({
          ...prev,
          isReactionInProgress: false,
          showReactionAnimation: false,
          // keep awaitingServerSync true until props reflect targetReaction
        }));
      } else {
        // No API call, just update local state
        setState(prev => ({
          ...prev,
          isReactionInProgress: false,
          showReactionAnimation: false,
          awaitingServerSync: false,
          targetReaction: reaction,
        }));
      }
    } catch (error: any) {
      // Rollback on error
      setState({
        ...prevState,
        showReactionAnimation: false,
        awaitingServerSync: false,
        targetReaction: prevState.currentReaction,
      });

      if (error?.name !== 'CanceledError') {
        // showMessage({
        //   message: 'Unable to add your reaction. Please try again',
        //   type: 'danger',
        //   position: 'bottom',
        // });
      }
    }
  }, [state, comment.uuid, currentUserId, onReactToComment, onShowReactionPicker]);

  /**
   * Remove current reaction
   */
  const removeReaction = useCallback(async () => {
    if (!state.currentReaction || state.isReactionInProgress) return;

    // Vibration feedback
    // Vibration.vibrate(50);

    // Analytics tracking
    /* ANALYTICS_DISABLED: DID_REMOVE_REACTION ('did_remove_reaction')
      analyticsService.trackEvent?.(AnalyticsEvent.DID_REMOVE_REACTION, {
            reaction_type: state.currentReaction,
            comment_id: comment.uuid,
          });
      */

    // Store previous state for rollback
    const prevState = cloneDeep(state);

    // Optimistic update
    const newByReactionUserIds = {
      ...state.byReactionUserIds,
      ...(state.byReactionUserIds[state.currentReaction]
        ? {
          [state.currentReaction]: state.byReactionUserIds[state.currentReaction].filter(
            id => id !== currentUserId,
          ),
        }
        : {}),
    };

    const sortedReactionUserIds = fromPairs(
      orderBy(toPairs(newByReactionUserIds), pair => pair[1].length, 'desc'),
    );

    setState({
      ...state,
      currentReaction: null,
      reactionsCount: sum(map(sortedReactionUserIds, value => value.length)) || 0,
      byReactionUserIds: sortedReactionUserIds,
      isReactionInProgress: true,
      targetReaction: null,
      awaitingServerSync: true,
    });

    try {
      if (onReactToComment) {
        // Make API call
        await onReactToComment(comment.uuid, null);

        // Finalize state (keep optimistic values)
        setState(prev => ({
          ...prev,
          isReactionInProgress: false,
          // keep awaitingServerSync true until props reflect targetReaction
        }));
      } else {
        // No API call, just update local state
        setState(prev => ({
          ...prev,
          isReactionInProgress: false,
          awaitingServerSync: false,
          targetReaction: null,
        }));
      }
    } catch (error: any) {
      // Rollback on error
      setState({ ...prevState, awaitingServerSync: false, targetReaction: prevState.currentReaction });

      if (error?.name !== 'CanceledError') {
        // showMessage({
        //   message: 'Unable to remove your reaction. Please try again',
        //   type: 'danger',
        //   position: 'bottom',
        // });
      }
    }
  }, [state, comment.uuid, currentUserId, onReactToComment]);

  /**
   * Toggle reaction picker visibility
   */
  const toggleReactionPicker = useCallback(() => {
    if (onShowReactionPicker) {
      onShowReactionPicker(state.showReactionPicker ? '' : comment.uuid);
    }
    setState(prev => ({
      ...prev,
      showReactionPicker: !prev.showReactionPicker,
    }));
  }, [state.showReactionPicker, comment.uuid, onShowReactionPicker]);

  /**
   * Show reaction modal with all users
   */
  const showReactionModal = useCallback(() => {
    if (onShowReactionModal) {
      onShowReactionModal(comment.uuid);
    }

    // Analytics tracking
    /* ANALYTICS_DISABLED: DID_VIEW_REACTIONS ('did_view_reactions')
      analyticsService.trackEvent?.(AnalyticsEvent.DID_VIEW_REACTIONS, {
            comment_id: comment.uuid,
          });
      */
  }, [comment.uuid, onShowReactionModal]);

  // Sync with incoming comment updates when not mid-request.
  // If we are awaiting server sync, only apply prop updates when they
  // reflect the targetReaction to avoid reverting optimistic UI.
  useEffect(() => {
    if (state.isReactionInProgress) return;

    const propsReaction = comment.myReaction || null;

    if (state.awaitingServerSync) {
      const propsMatchTarget = propsReaction === state.targetReaction;
      if (!propsMatchTarget) return;
    }

    setState(prev => ({
      ...prev,
      currentReaction: propsReaction,
      reactionsCount: comment.reactionsCount || 0,
      byReactionUserIds: comment.byReactionUserIds || {},
      awaitingServerSync: false,
    }));
  }, [comment.myReaction, comment.reactionsCount, comment.byReactionUserIds, state.isReactionInProgress, state.awaitingServerSync, state.targetReaction]);

  /**
   * Get reaction configuration by type
   */
  const getReactionConfig = useCallback((type: ReactionType) => {
    return reactionMap[type];
  }, [reactionMap]);

  /**
   * Get reaction color by type
   */
  const getReactionColor = useCallback((type: ReactionType) => {
    return reactionMap[type]?.color || '#666';
  }, [reactionMap]);

  /**
   * Format reaction count for display
   */
  const formatReactionCount = useCallback((count: number) => {
    if (count === 0) return '';
    const { reactionCount } = Strings.get().discussion.reactions;
    const template = count === 1 ? reactionCount.singular : reactionCount.plural;
    return Strings.format(template, { count: String(count) });
  }, []);

  /**
   * Check if user has reacted with specific reaction
   */
  const hasUserReacted = useCallback((reaction: ReactionType, userId: string) => {
    return state.byReactionUserIds[reaction]?.includes(userId) || false;
  }, [state.byReactionUserIds]);

  return {
    // State
    currentReaction: state.currentReaction,
    reactionsCount: state.reactionsCount,
    byReactionUserIds: state.byReactionUserIds,
    isReactionInProgress: state.isReactionInProgress,
    showReactionPicker: state.showReactionPicker,
    showReactionAnimation: state.showReactionAnimation,
    reactionsSorted,

    // Actions
    addReaction,
    removeReaction,
    toggleReactionPicker,
    showReactionModal,

    // UI helpers
    getReactionConfig,
    getReactionColor,
    formatReactionCount,
    hasUserReacted,
  };
}; 